// ── X-Ray Overlay ──
// Renders team-colored translucent materials with depthTest disabled
// so spectators can see player positions through walls.

import * as THREE from 'three';
import { type RemotePlayer } from '../gameplay/player';

interface StoredMeshMaterial {
  mesh: THREE.Mesh;
  original: THREE.Material | THREE.Material[];
}

export class XRayOverlay {
  private enabled = false;
  /** Per-player stored original materials, keyed by player id. */
  private storedMaterials: Map<number, StoredMeshMaterial[]> = new Map();
  private tMaterial: THREE.MeshBasicMaterial;
  private ctMaterial: THREE.MeshBasicMaterial;

  constructor() {
    // T material: orange, see-through
    this.tMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8b849,
      transparent: true,
      opacity: 0.6,
      depthTest: false,
      depthWrite: false,
    });

    // CT material: blue, see-through
    this.ctMaterial = new THREE.MeshBasicMaterial({
      color: 0x5b9bd5,
      transparent: true,
      opacity: 0.6,
      depthTest: false,
      depthWrite: false,
    });
  }

  /** Toggle X-ray on/off. Returns new state. */
  toggle(): boolean {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this.restoreAll();
    }
    return this.enabled;
  }

  /**
   * Apply/remove X-ray materials on all remote players. Call each frame when enabled.
   * Players must have a `team` property (1=T, 2=CT).
   */
  update(players: Map<number, RemotePlayer>): void {
    if (!this.enabled) {
      if (this.storedMaterials.size > 0) {
        this.restoreAll();
      }
      return;
    }

    // Drop entries for players that no longer exist (disconnect, replaced).
    for (const id of this.storedMaterials.keys()) {
      if (!players.has(id)) {
        this.restorePlayer(id);
      }
    }

    for (const [id, player] of players) {
      if (!player.mesh) continue;

      const teamMaterial = player.team === 1 ? this.tMaterial : this.ctMaterial;

      // Collect the current mesh set for this player.
      const currentMeshes: THREE.Mesh[] = [];
      player.mesh.traverse((child: THREE.Object3D) => {
        if (child instanceof THREE.Mesh) currentMeshes.push(child);
      });

      const stored = this.storedMaterials.get(id);

      // If the mesh set has changed (respawn, different model, prop swap),
      // the cached originals point at meshes that are no longer mounted — or
      // worse, point at our own xray materials that were captured after a
      // swap. Restore whatever we can for meshes that still exist and
      // re-capture fresh originals.
      if (!stored || !this.meshSetMatches(stored, currentMeshes)) {
        if (stored) {
          for (const entry of stored) {
            // Only restore if our xray material is still applied — otherwise
            // someone else owns the material and we shouldn't trample it.
            if (entry.mesh.material === this.tMaterial || entry.mesh.material === this.ctMaterial) {
              entry.mesh.material = entry.original;
            }
          }
        }
        const fresh: StoredMeshMaterial[] = currentMeshes.map((mesh) => ({
          mesh,
          original: mesh.material,
        }));
        this.storedMaterials.set(id, fresh);
      }

      // Apply team-colored X-ray material
      for (const mesh of currentMeshes) {
        mesh.material = teamMaterial;
      }
    }
  }

  /** Cheap equality check: same length and same mesh identities in order. */
  private meshSetMatches(stored: StoredMeshMaterial[], current: THREE.Mesh[]): boolean {
    if (stored.length !== current.length) return false;
    for (let i = 0; i < stored.length; i++) {
      if (stored[i].mesh !== current[i]) return false;
    }
    return true;
  }

  private restorePlayer(id: number): void {
    const entries = this.storedMaterials.get(id);
    if (!entries) return;
    for (const entry of entries) {
      if (entry.mesh.material === this.tMaterial || entry.mesh.material === this.ctMaterial) {
        entry.mesh.material = entry.original;
      }
    }
    this.storedMaterials.delete(id);
  }

  /** Check if X-ray is active. */
  get isActive(): boolean {
    return this.enabled;
  }

  /** Clean up materials. */
  dispose(): void {
    this.restoreAll();
    this.tMaterial.dispose();
    this.ctMaterial.dispose();
  }

  /** Restore original materials on all tracked players. */
  private restoreAll(): void {
    for (const [, entries] of this.storedMaterials) {
      for (const entry of entries) {
        entry.mesh.material = entry.original;
      }
    }
    this.storedMaterials.clear();
  }
}
